import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  CircleDollarSign,
  Lightbulb,
  MapPinned,
  Percent,
  Target,
  TrendingDown,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { Heading } from '../layout';
import { PillTabBar, SegmentedControl } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import API_BASE_URL from '../../config/api';
import { useBusinessUnitApi } from '../../contexts/BusinessUnitContext';
import {
  chileRegions,
  getChileRegionShortLabel,
  normalizeChileRegion,
} from '../../config/chileRegions';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  isDateInRange,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import {
  getActiveQuotationLossReasonOptions,
  loadCatalogItemsFromStorage,
  QUOTATION_LOSS_REASON_DEFAULTS,
} from './commercialCatalogDefaults';
import { parseLossCompetitor } from './quotationLossCompetitorUtils';

const CHILE_REGION_GEO_ORDER = chileRegions.reduce((acc, region, index) => {
  acc[region.value] = index;
  acc[region.shortLabel || region.value] = index;
  return acc;
}, {});

const chileRegionGeoRank = (value = '') => {
  const normalized = normalizeChileRegion(value) || String(value || '').trim();
  if (!normalized || normalized === 'Sin región' || normalized === 'S/R') return 999;
  if (CHILE_REGION_GEO_ORDER[normalized] != null) return CHILE_REGION_GEO_ORDER[normalized];
  const short = getChileRegionShortLabel(normalized);
  if (CHILE_REGION_GEO_ORDER[short] != null) return CHILE_REGION_GEO_ORDER[short];
  return 900;
};

const ANALYSIS_TABS = [
  { key: 'resumen', label: 'Resumen', icon: Lightbulb },
  { key: 'perdidas', label: 'Motivos de pérdida', icon: TrendingDown },
  { key: 'precio', label: 'Precio', icon: Target },
  { key: 'despacho', label: 'Despacho', icon: Truck },
];

const LOSS_METRIC_OPTIONS = [
  { key: 'count', label: 'Cantidad' },
  { key: 'amount', label: 'Montos' },
  { key: 'gallons', label: 'Galones' },
];

const MONTH_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const CARD_TONES = {
  dark: {
    light: { bg: '#0f172a', color: '#f8fafc', muted: '#94a3b8' },
    dark: { bg: '#020617', color: '#f8fafc', muted: '#94a3b8' },
  },
  mint: {
    light: { bg: '#d1fae5', color: '#064e3b', muted: '#047857' },
    dark: { bg: '#064e3b', color: '#d1fae5', muted: '#6ee7b7' },
  },
  rose: {
    light: { bg: '#fce7f3', color: '#9d174d', muted: '#be185d' },
    dark: { bg: '#831843', color: '#fce7f3', muted: '#f9a8d4' },
  },
  sky: {
    light: { bg: '#e0f2fe', color: '#0c4a6e', muted: '#0369a1' },
    dark: { bg: '#0c4a6e', color: '#e0f2fe', muted: '#7dd3fc' },
  },
  amber: {
    light: { bg: '#ffedd5', color: '#9a3412', muted: '#c2410c' },
    dark: { bg: '#7c2d12', color: '#ffedd5', muted: '#fdba74' },
  },
  slate: {
    light: { bg: '#e2e8f0', color: '#0f172a', muted: '#475569' },
    dark: { bg: '#1e293b', color: '#e2e8f0', muted: '#94a3b8' },
  },
};

const asArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.results)) return raw.results;
  }
  return [];
};

const PALETTE = {
  sales: '#0F766E',
  sky: '#0369A1',
  amber: '#B45309',
  green: '#15803D',
  coral: '#C2410C',
  rose: '#BE123C',
  slate: '#64748B',
  quoted: '#334155',
  trackLight: '#E2E8F0',
  trackDark: '#27272A',
};

/** Paleta categórica de alto contraste para motivos de pérdida (hues bien separados). */
const LOSS_REASON_COLORS = {
  PRECIO: '#E11D48',
  PLAZO: '#F59E0B',
  COMPETENCIA: '#7C3AED',
  PRESUPUESTO: '#2563EB',
  ESPECIFICACION: '#059669',
  CONDICIONES: '#0891B2',
  SIN_RESPUESTA: '#64748B',
  OTRO: '#A16207',
  SIN_MOTIVO: '#94A3B8',
};

const SERIES_COLORS = [
  '#E11D48',
  '#2563EB',
  '#F59E0B',
  '#059669',
  '#7C3AED',
  '#0891B2',
  '#EA580C',
  '#DB2777',
  '#0F766E',
  '#CA8A04',
];

const foldStatusKey = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

const normalizeStatus = (status = '') => {
  const key = foldStatusKey(status);
  const map = {
    aprobada: 'Adjudicada',
    adjudicada: 'Adjudicada',
    enviada: 'Emitida',
    emitida: 'Emitida',
    rechazada: 'Rechazada',
    perdida: 'Perdida',
    borrador: 'Borrador',
    desierta: 'Desierta',
    cancelada: 'Cancelada',
    vendida: 'Vendida',
  };
  if (map[key]) return map[key];
  return String(status || '').trim() || 'Borrador';
};

const quotationPrimaryDate = (q) => q?.quotation_date || q?.closing_date || q?.created_at || '';
const salePrimaryDate = (s) => s?.invoice_date || s?.deal_close_date || s?.quotation_date || s?.created_at || '';

const isClpLikeCurrency = (currency) => {
  const c = String(currency || 'CLP').trim().toUpperCase();
  return !c || c === 'CLP' || c === 'PESOS';
};

const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const commercialAmount = (row) => {
  if (!isClpLikeCurrency(row?.currency)) return 0;
  return grossAmountConIva(row?.total_amount);
};

const safeDivide = (num, den) => (den > 0 ? num / den : 0);
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000).toLocaleString('es-CL', { maximumFractionDigits: 0 })}M`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toLocaleString('es-CL');
};
const formatCurrencyCompact = (value) => `$ ${formatCompact(value)}`;
const formatCount = (value) => Number(value || 0).toLocaleString('es-CL');
const formatCurrencyFull = (value) =>
  `$ ${Math.round(Number(value || 0)).toLocaleString('es-CL')}`;

const filterByRange = (records, getDate, range) =>
  (Array.isArray(records) ? records : []).filter((row) => isDateInRange(getDate(row), range));

const trackBg = (ui) => (ui.isDarkMode ? PALETTE.trackDark : PALETTE.trackLight);

const MetricCard = ({ toneKey, isDarkMode, icon: Icon, label, value, hint }) => {
  const tone = (CARD_TONES[toneKey] || CARD_TONES.slate)[isDarkMode ? 'dark' : 'light'];
  return (
    <div
      style={{
        background: tone.bg,
        color: tone.color,
        borderRadius: 18,
        padding: '16px 18px',
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: tone.muted,
          }}
        >
          {label}
        </span>
        {Icon ? <Icon size={16} color={tone.muted} /> : null}
      </div>
      <div style={{ marginTop: 10, fontSize: 26, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
        {value}
      </div>
      {hint ? <div style={{ marginTop: 8, fontSize: 11, color: tone.muted }}>{hint}</div> : null}
    </div>
  );
};

const ContentPanel = ({ ui, headerLeft, headerRight, children }) => (
  <div
    style={{
      background: ui.panel,
      border: `1px solid ${ui.border}`,
      borderRadius: 12,
      overflow: 'hidden',
    }}
  >
    {(headerLeft || headerRight) && (
      <div
        style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {headerLeft || <span />}
        {headerRight || null}
      </div>
    )}
    <div style={{ padding: 12, display: 'grid', gap: 12 }}>{children}</div>
  </div>
);

const NestedPanel = ({ title, subtitle, right, ui, children }) => (
  <section
    style={{
      background: ui.panel,
      border: `1px solid ${ui.border}`,
      borderRadius: 10,
      padding: 12,
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    {(title || right) && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: subtitle ? 2 : 10,
          minWidth: 0,
          flexWrap: 'wrap',
        }}
      >
        {title ? (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 750,
              color: ui.title || ui.text,
              minWidth: 0,
            }}
          >
            {title}
          </p>
        ) : (
          <span />
        )}
        {right || null}
      </div>
    )}
    {subtitle ? (
      <p style={{ margin: '0 0 10px', fontSize: 11, color: ui.muted, lineHeight: 1.35 }}>{subtitle}</p>
    ) : null}
    <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>{children}</div>
  </section>
);

const isLostStatus = (status) => {
  const k = foldStatusKey(status);
  return k === 'perdida' || k === 'rechazada';
};

const isOpenStatus = (status) => {
  const k = foldStatusKey(status);
  return k === 'borrador' || k === 'emitida' || k === 'adjudicada';
};

const isDespachoMethod = (method) => {
  const k = foldStatusKey(method);
  return k.includes('despacho') || k.includes('envio') || k.includes('entrega');
};

const resolveLossReasonMeta = (label, options) => {
  const raw = String(label || '').trim();
  if (!raw) {
    return {
      label: 'Sin motivo',
      code: 'SIN_MOTIVO',
      color: LOSS_REASON_COLORS.SIN_MOTIVO,
    };
  }
  const match = (options || []).find(
    (opt) =>
      foldStatusKey(opt.value) === foldStatusKey(raw) ||
      foldStatusKey(opt.label) === foldStatusKey(raw) ||
      foldStatusKey(opt.code) === foldStatusKey(raw)
  );
  if (match) {
    const code = match.code || 'OTRO';
    return {
      label: match.label || raw,
      code,
      color: LOSS_REASON_COLORS[code] || SERIES_COLORS[Math.abs(code.length) % SERIES_COLORS.length],
    };
  }
  return { label: raw, code: 'OTRO', color: LOSS_REASON_COLORS.OTRO };
};

const InsightCard = ({ title, body, tone = 'info', ui }) => {
  const tones = {
    info: { border: PALETTE.sky, bg: ui.isDarkMode ? 'rgba(3,105,161,0.12)' : 'rgba(3,105,161,0.06)' },
    warn: { border: PALETTE.amber, bg: ui.isDarkMode ? 'rgba(180,83,9,0.14)' : 'rgba(180,83,9,0.08)' },
    danger: { border: PALETTE.coral, bg: ui.isDarkMode ? 'rgba(194,65,12,0.14)' : 'rgba(194,65,12,0.08)' },
    success: { border: PALETTE.green, bg: ui.isDarkMode ? 'rgba(21,128,61,0.14)' : 'rgba(21,128,61,0.08)' },
  };
  const t = tones[tone] || tones.info;
  return (
    <div
      style={{
        borderLeft: `3px solid ${t.border}`,
        background: t.bg,
        border: `1px solid ${ui.border}`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <p style={{ margin: 0, fontSize: 13, fontWeight: 750, color: ui.title || ui.text }}>{title}</p>
      <p style={{ margin: '6px 0 0', fontSize: 12, color: ui.muted, lineHeight: 1.45 }}>{body}</p>
    </div>
  );
};

const EmptyState = ({ ui, message = 'Sin datos en el período' }) => (
  <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{message}</p>
);

const quoteGallons = (quote) => {
  const n = Number(quote?.equivalent_gallons || 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const lossMetricValue = (quote, metric) => {
  if (metric === 'gallons') return quoteGallons(quote);
  if (metric === 'amount') return commercialAmount(quote);
  return 1;
};

const formatLossMetricValue = (value, metric) => {
  if (metric === 'amount') return formatCurrencyFull(value);
  if (metric === 'gallons') {
    return `${Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 1 })} gal`;
  }
  return formatCount(value);
};

const formatLossMetricTick = (value, metric) => {
  if (metric === 'amount') return formatCompact(value);
  if (metric === 'gallons') return Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  return formatCount(value);
};

const monthKeyFromDate = (value) => {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return iso.slice(0, 7);
};

const monthLabelFromKey = (key) => {
  const [year, month] = String(key || '').split('-');
  const idx = Number(month) - 1;
  if (!year || !(idx >= 0 && idx < 12)) return key || '—';
  return `${MONTH_SHORT_ES[idx]} ${String(year).slice(2)}`;
};

/** Construye filas apiladas: una categoría (región/mes) × motivos de pérdida. */
const buildStackedLossSeries = (quotes, getCategory, lossReasonOptions, metric) => {
  const categoryMap = new Map();
  const reasonMeta = new Map();
  (quotes || []).forEach((quote) => {
    const category = getCategory(quote);
    if (!category) return;
    const meta = resolveLossReasonMeta(quote.loss_reason, lossReasonOptions);
    reasonMeta.set(meta.label, meta);
    const bucket = categoryMap.get(category) || {
      key: category,
      name: category,
      total: 0,
      reasons: {},
    };
    const value = lossMetricValue(quote, metric);
    bucket.total += value;
    bucket.reasons[meta.label] = Number(bucket.reasons[meta.label] || 0) + value;
    categoryMap.set(category, bucket);
  });
  const reasonKeys = Array.from(reasonMeta.values())
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'))
    .map((meta) => meta.label);
  const rows = Array.from(categoryMap.values())
    .sort((a, b) => b.total - a.total || String(a.name).localeCompare(String(b.name), 'es'))
    .map((bucket) => {
      const row = { key: bucket.key, name: bucket.name, total: bucket.total };
      reasonKeys.forEach((reason) => {
        row[reason] = Number(bucket.reasons[reason] || 0);
      });
      return row;
    });
  return {
    rows,
    reasonKeys,
    reasonColors: Object.fromEntries(
      Array.from(reasonMeta.values()).map((meta) => [meta.label, meta.color])
    ),
  };
};

const LossStackedBarChart = ({
  title,
  subtitle,
  ui,
  metric,
  onMetricChange,
  rows,
  reasonKeys,
  reasonColors,
  emptyMessage,
  selection = null,
  onBarSelect,
}) => (
  <NestedPanel
    title={title}
    subtitle={subtitle}
    ui={ui}
    right={
      <SegmentedControl
        ariaLabel={`Métrica de ${title}`}
        items={LOSS_METRIC_OPTIONS}
        activeKey={metric}
        onChange={onMetricChange}
      />
    }
  >
    {rows.length && reasonKeys.length ? (
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={ui.isDarkMode ? '#3f3f46' : '#e2e8f0'} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: ui.muted }}
              interval={0}
              angle={rows.length > 6 ? -20 : 0}
              textAnchor={rows.length > 6 ? 'end' : 'middle'}
              height={rows.length > 6 ? 56 : 28}
            />
            <YAxis
              tick={{ fontSize: 10, fill: ui.muted }}
              tickFormatter={(value) => formatLossMetricTick(value, metric)}
              width={52}
            />
            <Tooltip
              contentStyle={chartTooltipStyle(ui)}
              formatter={(value, name) => [formatLossMetricValue(value, metric), name]}
              labelFormatter={(label, payload) => {
                const total = payload?.[0]?.payload?.total;
                return total != null
                  ? `${label} · total ${formatLossMetricValue(total, metric)}`
                  : label;
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {reasonKeys.map((reason, index) => (
              <Bar
                key={reason}
                dataKey={reason}
                stackId="loss"
                cursor="pointer"
                fill={reasonColors[reason] || SERIES_COLORS[index % SERIES_COLORS.length]}
                radius={index === reasonKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                onClick={(entry) => {
                  const payload = entry?.payload || {};
                  const next = {
                    categoryKey: payload.key,
                    categoryName: payload.name,
                  };
                  if (selection && selection.categoryKey === next.categoryKey) {
                    onBarSelect?.(null);
                    return;
                  }
                  onBarSelect?.(next);
                }}
              >
                {rows.map((row) => {
                  const dimmed = Boolean(selection?.categoryKey) && selection.categoryKey !== row.key;
                  return (
                    <Cell
                      key={`${reason}-${row.key}`}
                      fillOpacity={dimmed ? 0.28 : 1}
                    />
                  );
                })}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <EmptyState ui={ui} message={emptyMessage} />
    )}
  </NestedPanel>
);

const sideTableWrap = (ui) => ({
  overflow: 'auto',
  maxHeight: 680,
  border: `1px solid ${ui.border}`,
  borderRadius: 10,
});

const sideTh = (ui, align = 'left') => ({
  position: 'sticky',
  top: 0,
  background: ui.header,
  textAlign: align,
  padding: '8px 10px',
  fontSize: 11,
  fontWeight: 700,
  color: ui.muted,
  borderBottom: `1px solid ${ui.border}`,
  whiteSpace: 'nowrap',
  zIndex: 1,
});

const sideTd = (ui, align = 'left') => ({
  textAlign: align,
  padding: '8px 10px',
  fontSize: 12,
  color: ui.text,
  borderTop: `1px solid ${ui.border}`,
  verticalAlign: 'top',
});

const LostQuotesSidePanel = ({
  ui,
  selection,
  quotes,
  reasonOptions = [],
  reasonFilter = 'all',
  onReasonFilterChange,
  selectedQuoteId,
  onSelectQuote,
}) => {
  const reasonChoices = useMemo(() => {
    const labels = new Set();
    (quotes || []).forEach((quote) => {
      labels.add(resolveLossReasonMeta(quote.loss_reason, reasonOptions).label);
    });
    return Array.from(labels).sort((a, b) => String(a).localeCompare(String(b), 'es'));
  }, [quotes, reasonOptions]);

  const filteredQuotes = useMemo(() => {
    if (!reasonFilter || reasonFilter === 'all') return quotes || [];
    return (quotes || []).filter(
      (quote) => resolveLossReasonMeta(quote.loss_reason, reasonOptions).label === reasonFilter
    );
  }, [quotes, reasonFilter, reasonOptions]);

  return (
    <NestedPanel
      title="Cotizaciones perdidas"
      subtitle={
        selection
          ? `${selection.categoryName || selection.categoryKey} · ${filteredQuotes.length}${
              reasonFilter !== 'all' && quotes?.length !== filteredQuotes.length
                ? ` de ${quotes.length}`
                : ''
            }`
          : 'Haz clic en una barra del gráfico'
      }
      ui={ui}
      right={
        selection ? (
          <select
            aria-label="Filtrar por motivo de pérdida"
            value={reasonFilter}
            onChange={(event) => onReasonFilterChange?.(event.target.value)}
            style={{ ...periodSelectStyle(ui), minWidth: 150, maxWidth: 200, fontSize: 12 }}
          >
            <option value="all">Todos los motivos</option>
            {reasonChoices.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        ) : null
      }
    >
      {!selection ? (
        <EmptyState ui={ui} message="Selecciona una región o un mes en los gráficos." />
      ) : !(quotes || []).length ? (
        <EmptyState ui={ui} message="No hay cotizaciones perdidas en esta selección." />
      ) : filteredQuotes.length ? (
        <div style={sideTableWrap(ui)}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
            <thead>
              <tr>
                <th style={sideTh(ui)}>N° Cotización</th>
                <th style={sideTh(ui)}>Organización</th>
                <th style={sideTh(ui)}>Motivo</th>
                <th style={sideTh(ui, 'right')}>Precio ganador</th>
                <th style={sideTh(ui, 'right')}>Precio cotizado</th>
                <th style={sideTh(ui)}>Competidor ganador</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map((quote) => {
                const competitor = parseLossCompetitor(quote.loss_competitor || quote.loss_competitor_json);
                const quoted = commercialAmount(quote);
                const winnerNet = Number(competitor.competitor_amount || 0);
                const winner = winnerNet > 0 ? grossAmountConIva(winnerNet) : 0;
                const reason = resolveLossReasonMeta(quote.loss_reason, reasonOptions);
                const licitationCode =
                  String(quote.licitation_code || '').trim() ||
                  String(quote.tender || quote.tender_code || '').trim();
                const active = Number(selectedQuoteId) === Number(quote.id);
                return (
                  <tr
                    key={quote.id}
                    onClick={() => onSelectQuote?.(quote)}
                    style={{
                      cursor: 'pointer',
                      background: active
                        ? ui.isDarkMode
                          ? 'rgba(22, 163, 74, 0.18)'
                          : '#f0fdf4'
                        : 'transparent',
                    }}
                  >
                    <td style={{ ...sideTd(ui), fontWeight: 700 }}>
                      <div>{quote.quotation_code || quote.code || `#${quote.id}`}</div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: ui.muted, marginTop: 2 }}>
                        {licitationCode || 'Sin licitación'}
                      </div>
                    </td>
                    <td style={sideTd(ui)}>{quote.organization_name || '—'}</td>
                    <td style={sideTd(ui)}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 7px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 650,
                          background: ui.isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                          border: `1px solid ${reason.color || ui.border}`,
                          color: ui.text,
                          maxWidth: 140,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={reason.label}
                      >
                        {reason.label}
                      </span>
                    </td>
                    <td style={{ ...sideTd(ui, 'right'), fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {winner > 0 ? formatCurrencyCompact(winner) : '—'}
                    </td>
                    <td style={{ ...sideTd(ui, 'right'), fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {quoted > 0 ? formatCurrencyCompact(quoted) : '—'}
                    </td>
                    <td style={sideTd(ui)}>{competitor.competitor_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState ui={ui} message="Ninguna cotización coincide con el motivo seleccionado." />
      )}
    </NestedPanel>
  );
};

const LostQuoteItemsSidePanel = ({ ui, quote, items }) => (
  <NestedPanel
    title="Ítems de la cotización"
    subtitle={
      quote
        ? `${quote.quotation_code || quote.code || `#${quote.id}`} · ${items.length}`
        : 'Haz clic en una cotización'
    }
    ui={ui}
  >
    {!quote ? (
      <EmptyState ui={ui} message="Selecciona una cotización del listado." />
    ) : items.length ? (
      <div style={sideTableWrap(ui)}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 320 }}>
          <thead>
            <tr>
              <th style={sideTh(ui)}>Producto</th>
              <th style={sideTh(ui, 'right')}>Cant.</th>
              <th style={sideTh(ui, 'right')}>Precio cotizado</th>
              <th style={sideTh(ui, 'right')}>Precio ganador</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id || `${item.product_sku}-${item.item_name}`}>
                <td style={sideTd(ui)}>
                  <div style={{ fontWeight: 600 }}>{item.product_name || item.item_name || '—'}</div>
                  <div style={{ fontSize: 11, color: ui.muted }}>
                    {[item.product_sku, item.color || item.color_code].filter(Boolean).join(' · ')}
                  </div>
                  {item.competitor_product ? (
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                      Comp.: {item.competitor_product}
                    </div>
                  ) : null}
                </td>
                <td style={{ ...sideTd(ui, 'right'), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatCount(item.quantity)} {item.unit || ''}
                </td>
                <td style={{ ...sideTd(ui, 'right'), fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {item.quoted_unit_price > 0 ? formatCurrencyCompact(item.quoted_unit_price) : '—'}
                </td>
                <td style={{ ...sideTd(ui, 'right'), fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {item.winner_unit_price > 0 ? formatCurrencyCompact(item.winner_unit_price) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <EmptyState ui={ui} message="Esta cotización no tiene ítems registrados." />
    )}
  </NestedPanel>
);

const BreakdownBars = ({ rows, ui, valueMode = 'currency' }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  if (!total) return <EmptyState ui={ui} />;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((row) => {
        const value = Number(row.value || 0);
        const pct = safeDivide(value, total) * 100;
        const display =
          valueMode === 'count' ? formatCount(value) : formatCurrencyCompact(value);
        return (
          <div key={row.key || row.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 3,
                fontSize: 11,
              }}
            >
              <span style={{ color: ui.text, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.label}
              </span>
              <span style={{ color: ui.muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {display} · {formatPercent(pct)}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: trackBg(ui), overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.max(pct, 1.5)}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: row.color || PALETTE.coral,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const chartTooltipStyle = (ui) => ({
  background: ui.isDarkMode ? '#18181b' : '#fff',
  border: `1px solid ${ui.border}`,
  borderRadius: 10,
  fontSize: 12,
});

const tableHeaderStyle = (ui) => ({
  padding: '8px 10px',
  fontWeight: 700,
  fontSize: 11,
  color: ui.muted,
  background: ui.header,
  borderBottom: `1px solid ${ui.border}`,
  whiteSpace: 'nowrap',
});

const tableCellStyle = (ui, align = 'left') => ({
  padding: '8px 10px',
  borderTop: `1px solid ${ui.border}`,
  textAlign: align,
  color: ui.text,
  fontSize: 12,
});

const PaintCommercialAnalysisWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { authFetch } = useBusinessUnitApi();
  const [quotations, setQuotations] = useState([]);
  const [quotationItems, setQuotationItems] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [analysisTab, setAnalysisTab] = useState('resumen');
  const [regionLossMetric, setRegionLossMetric] = useState('amount');
  const [monthLossMetric, setMonthLossMetric] = useState('amount');
  const [lossBarSelection, setLossBarSelection] = useState(null);
  const [selectedLostQuoteId, setSelectedLostQuoteId] = useState(null);
  const [lostQuoteReasonFilter, setLostQuoteReasonFilter] = useState('all');

  const lossReasonOptions = useMemo(() => {
    const stored = loadCatalogItemsFromStorage('pinturas');
    const items = stored?.loss_reason || QUOTATION_LOSS_REASON_DEFAULTS;
    return getActiveQuotationLossReasonOptions(items);
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [quotesRes, itemsRes, salesRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/quotations/`),
        authFetch(`${API_BASE_URL}/api/quotations/items`),
        authFetch(`${API_BASE_URL}/api/quotations/sales`),
      ]);
      if (!quotesRes.ok) throw new Error(`Quotations HTTP ${quotesRes.status}`);
      if (!itemsRes.ok) throw new Error(`Quotation items HTTP ${itemsRes.status}`);
      if (!salesRes.ok) throw new Error(`Sales HTTP ${salesRes.status}`);
      const quotesRaw = await quotesRes.json();
      const itemsRaw = await itemsRes.json();
      const salesRaw = await salesRes.json();
      setQuotations(
        asArray(quotesRaw).map((quote) => ({
          ...quote,
          status: normalizeStatus(quote.status),
        }))
      );
      setQuotationItems(asArray(itemsRaw));
      setSales(asArray(salesRaw));
    } catch (fetchError) {
      console.error('Error loading commercial analysis:', fetchError);
      setQuotations([]);
      setQuotationItems([]);
      setSales([]);
      setError('No se pudo cargar el análisis comercial.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const allDateValues = useMemo(
    () => [...collectDates(quotations, quotationPrimaryDate), ...collectDates(sales, salePrimaryDate)],
    [quotations, sales]
  );
  const availableYears = useMemo(() => getAvailableYearsFromDates(allDateValues), [allDateValues]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: allDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Análisis comercial mensual',
          annual: 'Análisis comercial anual',
        },
      }),
    [periodFilter, allDateValues, secondaryYear, secondaryMonth]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly' && !availableYears.includes(Number(secondaryYear))) {
      setSecondaryYear(String(availableYears[availableYears.length - 1]));
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const periodQuotes = useMemo(
    () => filterByRange(quotations, quotationPrimaryDate, selectedPeriod.range),
    [quotations, selectedPeriod]
  );
  const periodSales = useMemo(
    () => filterByRange(sales, salePrimaryDate, selectedPeriod.range),
    [sales, selectedPeriod]
  );

  const lostQuotes = useMemo(
    () => periodQuotes.filter((q) => isLostStatus(q.status)),
    [periodQuotes]
  );
  const soldQuotes = useMemo(
    () => periodQuotes.filter((q) => foldStatusKey(q.status) === 'vendida'),
    [periodQuotes]
  );
  const openQuotes = useMemo(
    () => periodQuotes.filter((q) => isOpenStatus(q.status) && foldStatusKey(q.status) !== 'adjudicada'),
    [periodQuotes]
  );

  const metrics = useMemo(() => {
    const quotedAmount = periodQuotes.reduce((sum, q) => sum + commercialAmount(q), 0);
    const soldAmount = soldQuotes.reduce((sum, q) => sum + commercialAmount(q), 0);
    const lostAmount = lostQuotes.reduce((sum, q) => sum + commercialAmount(q), 0);
    const openAmount = openQuotes.reduce((sum, q) => sum + commercialAmount(q), 0);
    const decided = soldQuotes.length + lostQuotes.length;
    return {
      totalQuotes: periodQuotes.length,
      quotedAmount,
      soldCount: soldQuotes.length,
      soldAmount,
      lostCount: lostQuotes.length,
      lostAmount,
      openCount: openQuotes.length,
      openAmount,
      winRate: safeDivide(soldQuotes.length, decided) * 100,
      lossShare: safeDivide(lostAmount, soldAmount + lostAmount) * 100,
      avgLostTicket: safeDivide(lostAmount, lostQuotes.length),
      avgSoldTicket: safeDivide(soldAmount, soldQuotes.length),
    };
  }, [periodQuotes, soldQuotes, lostQuotes, openQuotes]);

  const lossReasonRows = useMemo(() => {
    const map = new Map();
    lostQuotes.forEach((quote) => {
      const meta = resolveLossReasonMeta(quote.loss_reason, lossReasonOptions);
      const prev = map.get(meta.label) || {
        key: meta.code + meta.label,
        label: meta.label,
        code: meta.code,
        color: meta.color,
        count: 0,
        amount: 0,
      };
      prev.count += 1;
      prev.amount += commercialAmount(quote);
      map.set(meta.label, prev);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount || b.count - a.count);
  }, [lostQuotes, lossReasonOptions]);

  const lossReasonChartData = useMemo(
    () =>
      lossReasonRows.map((row) => ({
        name: row.label.length > 22 ? `${row.label.slice(0, 20)}…` : row.label,
        fullName: row.label,
        amount: Math.round(row.amount),
        count: row.count,
        color: row.color,
      })),
    [lossReasonRows]
  );

  const priceLossAnalysis = useMemo(() => {
    const priceLost = lostQuotes.filter((q) => {
      const meta = resolveLossReasonMeta(q.loss_reason, lossReasonOptions);
      return meta.code === 'PRECIO' || foldStatusKey(meta.label).includes('precio');
    });

    const withCompetitor = [];
    lostQuotes.forEach((quote) => {
      const competitor = parseLossCompetitor(quote.loss_competitor || quote.loss_competitor_json);
      const ourNet = Number(quote.total_amount || 0);
      const theirNet = Number(competitor.competitor_amount || 0);
      if (!(ourNet > 0) || !(theirNet > 0)) return;
      const gapPct = ((ourNet - theirNet) / theirNet) * 100;
      const gapAmount = ourNet - theirNet;
      withCompetitor.push({
        id: quote.id,
        code: quote.quotation_code || quote.code || `#${quote.id}`,
        organization: quote.organization_name || 'Sin organización',
        region: normalizeChileRegion(quote.location_region) || 'Sin región',
        ourAmount: grossAmountConIva(ourNet),
        competitorAmount: grossAmountConIva(theirNet),
        competitorName: competitor.competitor_name || 'Competencia',
        gapPct,
        gapAmount: grossAmountConIva(gapAmount),
        lossReason: resolveLossReasonMeta(quote.loss_reason, lossReasonOptions).label,
        comment: String(quote.loss_buyer_comment || '').trim(),
      });
    });

    const overpriced = withCompetitor
      .filter((row) => row.gapPct > 0)
      .sort((a, b) => b.gapPct - a.gapPct || b.gapAmount - a.gapAmount);

    const avgOverGap =
      overpriced.length > 0
        ? overpriced.reduce((sum, row) => sum + row.gapPct, 0) / overpriced.length
        : 0;

    const competitorMap = new Map();
    withCompetitor.forEach((row) => {
      const key = row.competitorName || 'Competencia';
      const prev = competitorMap.get(key) || { name: key, count: 0, amount: 0 };
      prev.count += 1;
      prev.amount += row.ourAmount;
      competitorMap.set(key, prev);
    });
    const topCompetitors = Array.from(competitorMap.values()).sort(
      (a, b) => b.count - a.count || b.amount - a.amount
    );

    return {
      priceLostCount: priceLost.length,
      priceLostAmount: priceLost.reduce((sum, q) => sum + commercialAmount(q), 0),
      withCompetitorCount: withCompetitor.length,
      overpriced,
      avgOverGap,
      topCompetitors,
    };
  }, [lostQuotes, lossReasonOptions]);

  const shippingByRegion = useMemo(() => {
    const map = new Map();
    periodSales.forEach((sale) => {
      const method = sale.delivery_method || sale.quotation_delivery_method || '';
      const cost = Number(sale.dispatch_cost_total || 0);
      if (!(cost > 0)) return;
      if (method && !isDespachoMethod(method) && foldStatusKey(method).includes('retiro')) return;
      const region = normalizeChileRegion(sale.organization_region || sale.location_region) || 'Sin región';
      const prev = map.get(region) || {
        region,
        short: getChileRegionShortLabel(region),
        count: 0,
        cost: 0,
        saleAmount: 0,
      };
      prev.count += 1;
      prev.cost += cost;
      prev.saleAmount += commercialAmount(sale);
      map.set(region, prev);
    });
    return Array.from(map.values())
      .map((row) => ({
        ...row,
        avgCost: safeDivide(row.cost, row.count),
        costShareOfSale: safeDivide(row.cost, row.saleAmount) * 100,
      }))
      .sort((a, b) => b.avgCost - a.avgCost || b.cost - a.cost);
  }, [periodSales]);

  const shippingChartData = useMemo(
    () =>
      shippingByRegion.slice(0, 10).map((row) => ({
        name: row.short,
        fullName: row.region,
        avgCost: Math.round(row.avgCost),
        totalCost: Math.round(row.cost),
        count: row.count,
      })),
    [shippingByRegion]
  );

  const regionLossStacked = useMemo(() => {
    const series = buildStackedLossSeries(
      lostQuotes,
      (quote) => {
        const region = normalizeChileRegion(quote.location_region) || 'Sin región';
        return getChileRegionShortLabel(region) || region;
      },
      lossReasonOptions,
      regionLossMetric
    );
    const rows = [...series.rows].sort((a, b) => {
      const rankDiff = chileRegionGeoRank(a.key) - chileRegionGeoRank(b.key);
      if (rankDiff !== 0) return rankDiff;
      return String(a.name).localeCompare(String(b.name), 'es');
    });
    return { ...series, rows };
  }, [lostQuotes, lossReasonOptions, regionLossMetric]);

  const monthLossStacked = useMemo(() => {
    const series = buildStackedLossSeries(
      lostQuotes,
      (quote) => monthKeyFromDate(quotationPrimaryDate(quote)),
      lossReasonOptions,
      monthLossMetric
    );
    const rows = [...series.rows]
      .sort((a, b) => String(a.key).localeCompare(String(b.key)))
      .map((row) => ({ ...row, name: monthLabelFromKey(row.key) }));
    return { ...series, rows };
  }, [lostQuotes, lossReasonOptions, monthLossMetric]);

  useEffect(() => {
    setLossBarSelection(null);
    setSelectedLostQuoteId(null);
    setLostQuoteReasonFilter('all');
  }, [selectedPeriod.range?.start, selectedPeriod.range?.end, analysisTab]);

  useEffect(() => {
    setLostQuoteReasonFilter('all');
    setSelectedLostQuoteId(null);
  }, [lossBarSelection?.source, lossBarSelection?.categoryKey]);

  const selectedLostQuotes = useMemo(() => {
    if (!lossBarSelection) return [];
    return lostQuotes.filter((quote) => {
      if (lossBarSelection.source === 'region') {
        const region = normalizeChileRegion(quote.location_region) || 'Sin región';
        const short = getChileRegionShortLabel(region) || region;
        return short === lossBarSelection.categoryKey || region === lossBarSelection.categoryKey;
      }
      if (lossBarSelection.source === 'month') {
        return monthKeyFromDate(quotationPrimaryDate(quote)) === lossBarSelection.categoryKey;
      }
      return false;
    });
  }, [lossBarSelection, lostQuotes]);

  const filteredLostQuotes = useMemo(() => {
    if (!lostQuoteReasonFilter || lostQuoteReasonFilter === 'all') return selectedLostQuotes;
    return selectedLostQuotes.filter(
      (quote) =>
        resolveLossReasonMeta(quote.loss_reason, lossReasonOptions).label === lostQuoteReasonFilter
    );
  }, [selectedLostQuotes, lostQuoteReasonFilter, lossReasonOptions]);

  const selectedLostQuote = useMemo(
    () => filteredLostQuotes.find((quote) => Number(quote.id) === Number(selectedLostQuoteId)) || null,
    [filteredLostQuotes, selectedLostQuoteId]
  );

  const selectedLostQuoteItems = useMemo(() => {
    if (!selectedLostQuote) return [];
    const competitor = parseLossCompetitor(
      selectedLostQuote.loss_competitor || selectedLostQuote.loss_competitor_json
    );
    const competitorByItemId = new Map(
      (competitor.line_items || []).map((entry) => [Number(entry.quotation_item_id), entry])
    );
    return quotationItems
      .filter((item) => Number(item.quotation_id) === Number(selectedLostQuote.id))
      .map((item) => {
        const competitorLine = competitorByItemId.get(Number(item.id)) || {};
        const quotedUnit = Number(item.unit_price || 0);
        const winnerUnit = Number(competitorLine.competitor_unit_price || 0);
        return {
          ...item,
          competitor_product: competitorLine.competitor_product || '',
          quoted_unit_price: quotedUnit > 0 ? grossAmountConIva(quotedUnit) : 0,
          winner_unit_price: winnerUnit > 0 ? grossAmountConIva(winnerUnit) : 0,
        };
      });
  }, [selectedLostQuote, quotationItems]);

  const insights = useMemo(() => {
    const items = [];
    const topReason = lossReasonRows[0];
    if (topReason) {
      items.push({
        tone: topReason.code === 'PRECIO' ? 'danger' : 'warn',
        title: `Principal motivo: ${topReason.label}`,
        body: `${formatCount(topReason.count)} pérdidas por ${formatCurrencyCompact(topReason.amount)} c/IVA. Concentra la revisión comercial ahí primero.`,
      });
    }

    if (priceLossAnalysis.overpriced.length > 0) {
      const top = priceLossAnalysis.overpriced[0];
      items.push({
        tone: 'danger',
        title: 'Priorizar baja de precio',
        body: `${top.code} (${top.organization}) quedó ${formatPercent(top.gapPct)} sobre ${top.competitorName}. Hay ${formatCount(priceLossAnalysis.overpriced.length)} pérdidas con gap de precio documentado; promedio ${formatPercent(priceLossAnalysis.avgOverGap)}.`,
      });
    } else if (priceLossAnalysis.priceLostCount > 0) {
      items.push({
        tone: 'warn',
        title: 'Pérdidas por precio sin benchmark',
        body: `${formatCount(priceLossAnalysis.priceLostCount)} cotizaciones se perdieron por precio (${formatCurrencyCompact(priceLossAnalysis.priceLostAmount)}), pero sin monto de competencia. Completa el cierre con precio competidor para priorizar mejor.`,
      });
    }

    if (shippingByRegion.length > 0) {
      const topShip = shippingByRegion[0];
      const cheapest = [...shippingByRegion].sort((a, b) => a.avgCost - b.avgCost)[0];
      items.push({
        tone: 'info',
        title: `Despacho más caro: ${topShip.region}`,
        body: `Promedio ${formatCurrencyCompact(topShip.avgCost)} en ${formatCount(topShip.count)} ventas (${formatPercent(topShip.costShareOfSale)} del ticket). Contrasta con ${cheapest.region} a ${formatCurrencyCompact(cheapest.avgCost)}.`,
      });
    }

    if (metrics.winRate > 0 && metrics.winRate < 35 && metrics.lostCount >= 3) {
      items.push({
        tone: 'warn',
        title: 'Tasa de cierre baja',
        body: `Solo ${formatPercent(metrics.winRate)} de las cotizaciones resueltas se vendieron. Revisa pipeline abierto (${formatCount(metrics.openCount)}, ${formatCurrencyCompact(metrics.openAmount)}) antes de cotizar más volumen.`,
      });
    }

    if (!items.length) {
      items.push({
        tone: 'success',
        title: 'Sin señales críticas',
        body: 'Aún no hay suficientes pérdidas o costos de despacho en el período para priorizar acciones. Amplía el filtro o completa motivos de pérdida al cerrar.',
      });
    }
    return items.slice(0, 4);
  }, [lossReasonRows, priceLossAnalysis, shippingByRegion, metrics]);

  const periodHelper = PERIOD_OPTIONS.find((option) => option.value === periodFilter)?.label || selectedPeriod.title;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 16,
          rowGap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Análisis
          </Heading>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: 10,
            flexWrap: 'wrap',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: 160 }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' ||
          periodFilter === 'annual_ytd' ||
          periodFilter === 'quarterly' ||
          periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: 100 }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 90 }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 120 }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div
          style={{
            marginTop: 16,
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: 24,
            color: ui.muted,
            fontSize: 13,
          }}
        >
          Cargando análisis…
        </div>
      ) : null}
      {error ? (
        <div
          style={{
            marginTop: 16,
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: 24,
            color: ui.danger || PALETTE.rose,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      {!loading && !error ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <MetricCard
              toneKey="dark"
              isDarkMode={isDarkMode}
              icon={CircleDollarSign}
              label="Cotizado c/IVA"
              value={formatCurrencyCompact(metrics.quotedAmount)}
              hint={`${formatCount(metrics.totalQuotes)} cotizaciones`}
            />
            <MetricCard
              toneKey="mint"
              isDarkMode={isDarkMode}
              icon={TrendingUp}
              label="Vendido c/IVA"
              value={formatCurrencyCompact(metrics.soldAmount)}
              hint={`${formatCount(metrics.soldCount)} · ticket ${formatCurrencyCompact(metrics.avgSoldTicket)}`}
            />
            <MetricCard
              toneKey="rose"
              isDarkMode={isDarkMode}
              icon={TrendingDown}
              label="Perdido c/IVA"
              value={formatCurrencyCompact(metrics.lostAmount)}
              hint={`${formatCount(metrics.lostCount)} · ticket ${formatCurrencyCompact(metrics.avgLostTicket)}`}
            />
            <MetricCard
              toneKey="sky"
              isDarkMode={isDarkMode}
              icon={Percent}
              label="Tasa de cierre"
              value={formatPercent(metrics.winRate)}
              hint={`${metrics.soldCount} vend. / ${metrics.soldCount + metrics.lostCount} resueltas`}
            />
            <MetricCard
              toneKey="slate"
              isDarkMode={isDarkMode}
              icon={Activity}
              label="Pipeline abierto"
              value={formatCurrencyCompact(metrics.openAmount)}
              hint={`${formatCount(metrics.openCount)} en curso`}
            />
            <MetricCard
              toneKey="amber"
              isDarkMode={isDarkMode}
              icon={MapPinned}
              label="Peso de pérdidas"
              value={formatPercent(metrics.lossShare)}
              hint="Perdido / (vendido + perdido)"
            />
          </div>

          <ContentPanel
            ui={ui}
            headerLeft={
              <PillTabBar
                ariaLabel="Vista del análisis comercial"
                items={ANALYSIS_TABS}
                activeKey={analysisTab}
                onChange={setAnalysisTab}
              />
            }
            headerRight={
              <span style={{ fontSize: 11, color: ui.muted }}>
                {selectedPeriod.title} · {periodHelper}
              </span>
            }
          >
            {analysisTab === 'resumen' ? (
              <NestedPanel
                title="Acciones sugeridas"
                subtitle="Prioridades a partir de los datos del período"
                ui={ui}
              >
                <div style={{ display: 'grid', gap: 10 }}>
                  {insights.map((insight) => (
                    <InsightCard key={insight.title} {...insight} ui={ui} />
                  ))}
                </div>
              </NestedPanel>
            ) : null}

            {analysisTab === 'perdidas' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.05fr) minmax(340px, 0.95fr) minmax(280px, 0.8fr)',
                    gap: 12,
                    alignItems: 'start',
                  }}
                >
                  <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
                    <LossStackedBarChart
                      title="Pérdidas por región"
                      subtitle="Clic en una barra para listar todas las cotizaciones de esa región"
                      ui={ui}
                      metric={regionLossMetric}
                      onMetricChange={setRegionLossMetric}
                      rows={regionLossStacked.rows}
                      reasonKeys={regionLossStacked.reasonKeys}
                      reasonColors={regionLossStacked.reasonColors}
                      emptyMessage="No hay cotizaciones perdidas por región en el período"
                      selection={lossBarSelection?.source === 'region' ? lossBarSelection : null}
                      onBarSelect={(next) => {
                        setSelectedLostQuoteId(null);
                        setLossBarSelection(next ? { ...next, source: 'region' } : null);
                      }}
                    />
                    <LossStackedBarChart
                      title="Pérdidas por mes"
                      subtitle="Clic en una barra para listar todas las cotizaciones de ese mes"
                      ui={ui}
                      metric={monthLossMetric}
                      onMetricChange={setMonthLossMetric}
                      rows={monthLossStacked.rows}
                      reasonKeys={monthLossStacked.reasonKeys}
                      reasonColors={monthLossStacked.reasonColors}
                      emptyMessage="No hay cotizaciones perdidas por mes en el período"
                      selection={lossBarSelection?.source === 'month' ? lossBarSelection : null}
                      onBarSelect={(next) => {
                        setSelectedLostQuoteId(null);
                        setLossBarSelection(next ? { ...next, source: 'month' } : null);
                      }}
                    />
                  </div>
                  <LostQuotesSidePanel
                    ui={ui}
                    selection={lossBarSelection}
                    quotes={selectedLostQuotes}
                    reasonOptions={lossReasonOptions}
                    reasonFilter={lostQuoteReasonFilter}
                    onReasonFilterChange={(value) => {
                      setLostQuoteReasonFilter(value || 'all');
                      setSelectedLostQuoteId(null);
                    }}
                    selectedQuoteId={selectedLostQuoteId}
                    onSelectQuote={(quote) => setSelectedLostQuoteId(quote?.id || null)}
                  />
                  <LostQuoteItemsSidePanel
                    ui={ui}
                    quote={selectedLostQuote}
                    items={selectedLostQuoteItems}
                  />
                </div>
                {lossReasonRows.length ? (
                  <NestedPanel
                    title="Resumen de motivos"
                    subtitle="Participación de cada motivo en el monto perdido del período"
                    ui={ui}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
                        gap: 12,
                        alignItems: 'start',
                      }}
                    >
                      <div style={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                          <BarChart data={lossReasonChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={ui.isDarkMode ? '#3f3f46' : '#e2e8f0'} />
                            <XAxis
                              dataKey="name"
                              tick={{ fontSize: 10, fill: ui.muted }}
                              interval={0}
                              angle={-18}
                              textAnchor="end"
                              height={56}
                            />
                            <YAxis
                              tick={{ fontSize: 10, fill: ui.muted }}
                              tickFormatter={(v) => formatCompact(v)}
                              width={48}
                            />
                            <Tooltip
                              contentStyle={chartTooltipStyle(ui)}
                              formatter={(value, _name, props) => [
                                `${formatCurrencyFull(value)} · ${formatCount(props?.payload?.count)} casos`,
                                props?.payload?.fullName || 'Monto',
                              ]}
                            />
                            <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                              {lossReasonChartData.map((entry) => (
                                <Cell key={entry.fullName} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <BreakdownBars
                        ui={ui}
                        rows={lossReasonRows.map((row) => ({
                          key: row.key,
                          label: row.label,
                          value: row.amount,
                          color: row.color,
                        }))}
                      />
                    </div>
                  </NestedPanel>
                ) : null}
              </div>
            ) : null}

            {analysisTab === 'precio' ? (
              <NestedPanel
                title="Priorizar baja de precio"
                subtitle="Pérdidas con precio de competencia: ordenadas por cuánto más caros quedamos"
                ui={ui}
                right={
                  priceLossAnalysis.avgOverGap > 0 ? (
                    <span style={{ fontSize: 11, color: PALETTE.coral, fontWeight: 700 }}>
                      Gap promedio {formatPercent(priceLossAnalysis.avgOverGap)}
                    </span>
                  ) : null
                }
              >
                {priceLossAnalysis.overpriced.length ? (
                  <div
                    style={{
                      overflowX: 'auto',
                      border: `1px solid ${ui.border}`,
                      borderRadius: 10,
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'left' }}>Cotización</th>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'left' }}>Cliente</th>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'left' }}>Competencia</th>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>Nuestro</th>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>Ellos</th>
                          <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>Gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priceLossAnalysis.overpriced.slice(0, 12).map((row) => (
                          <tr key={row.id}>
                            <td style={{ ...tableCellStyle(ui), fontWeight: 600 }}>{row.code}</td>
                            <td style={tableCellStyle(ui)}>
                              <div>{row.organization}</div>
                              <div style={{ fontSize: 10, color: ui.muted }}>{row.region}</div>
                            </td>
                            <td style={{ ...tableCellStyle(ui), color: ui.muted }}>{row.competitorName}</td>
                            <td style={{ ...tableCellStyle(ui, 'right'), fontVariantNumeric: 'tabular-nums' }}>
                              {formatCurrencyCompact(row.ourAmount)}
                            </td>
                            <td style={{ ...tableCellStyle(ui, 'right'), fontVariantNumeric: 'tabular-nums' }}>
                              {formatCurrencyCompact(row.competitorAmount)}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle(ui, 'right'),
                                fontWeight: 700,
                                color: PALETTE.coral,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {`+${row.gapPct.toFixed(1)}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState
                    ui={ui}
                    message={
                      priceLossAnalysis.priceLostCount
                        ? 'Hay pérdidas por precio, pero sin monto de competencia registrado al cierre.'
                        : 'No hay pérdidas con gap de precio documentado en el período.'
                    }
                  />
                )}
                {priceLossAnalysis.topCompetitors.length ? (
                  <div style={{ marginTop: 12 }}>
                    <p
                      style={{
                        margin: '0 0 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: ui.muted,
                      }}
                    >
                      Competidores más frecuentes en pérdidas
                    </p>
                    <BreakdownBars
                      ui={ui}
                      valueMode="count"
                      rows={priceLossAnalysis.topCompetitors.slice(0, 5).map((row, index) => ({
                        key: row.name,
                        label: row.name,
                        value: row.count,
                        color: SERIES_COLORS[index % SERIES_COLORS.length],
                      }))}
                    />
                  </div>
                ) : null}
              </NestedPanel>
            ) : null}

            {analysisTab === 'despacho' ? (
              <NestedPanel
                title="Despacho por zona"
                subtitle="Costo promedio de despacho en ventas con costo asignado (excluye retiros cuando el método está marcado)"
                ui={ui}
              >
                {shippingChartData.length ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
                      gap: 12,
                      alignItems: 'start',
                    }}
                  >
                    <div style={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer>
                        <BarChart data={shippingChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={ui.isDarkMode ? '#3f3f46' : '#e2e8f0'} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: ui.muted }} />
                          <YAxis
                            tick={{ fontSize: 10, fill: ui.muted }}
                            tickFormatter={(v) => formatCompact(v)}
                            width={48}
                          />
                          <Tooltip
                            contentStyle={chartTooltipStyle(ui)}
                            formatter={(value, name, props) => {
                              if (name === 'avgCost') {
                                return [formatCurrencyFull(value), `Promedio · ${props?.payload?.fullName || ''}`];
                              }
                              return [formatCurrencyFull(value), 'Total'];
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="avgCost" name="Promedio" fill={PALETTE.sky} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="totalCost" name="Total" fill={PALETTE.amber} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div
                      style={{
                        overflowX: 'auto',
                        border: `1px solid ${ui.border}`,
                        borderRadius: 10,
                      }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...tableHeaderStyle(ui), textAlign: 'left' }}>Región</th>
                            <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>Ventas</th>
                            <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>Promedio</th>
                            <th style={{ ...tableHeaderStyle(ui), textAlign: 'right' }}>% ticket</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shippingByRegion.slice(0, 10).map((row) => (
                            <tr key={row.region}>
                              <td style={{ ...tableCellStyle(ui), fontWeight: 600 }}>{row.region}</td>
                              <td style={{ ...tableCellStyle(ui, 'right'), fontVariantNumeric: 'tabular-nums' }}>
                                {formatCount(row.count)}
                              </td>
                              <td style={{ ...tableCellStyle(ui, 'right'), fontVariantNumeric: 'tabular-nums' }}>
                                {formatCurrencyCompact(row.avgCost)}
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle(ui, 'right'),
                                  fontVariantNumeric: 'tabular-nums',
                                  color: ui.muted,
                                }}
                              >
                                {formatPercent(row.costShareOfSale)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <EmptyState ui={ui} message="No hay costos de despacho registrados en ventas del período." />
                )}
              </NestedPanel>
            ) : null}
          </ContentPanel>
        </div>
      ) : null}
    </div>
  );
};

export default PaintCommercialAnalysisWorkspace;
